import * as Yup from 'yup';

// Validation schema for Event Details Tab
export const eventDetailsValidationSchema = Yup.object().shape({
  // Event Name - Required field
  eventName: Yup.string()
    .required('Event name is required')
    .min(3, 'Event name must be at least 3 characters')
    .max(100, 'Event name must be less than 100 characters')
    .trim(),

  // Event Date - Required field
  eventDate: Yup.string()
    .required('Event date is required')
    .test('future-date', 'Event date must be in the future', function(value) {
      if (!value) return false;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }),

  // Event Time - Required field
  eventTime: Yup.string()
    .required('Start time is required'),

  // Event End Time - Optional field
  eventEndTime: Yup.string()
    .optional(),

  // Is Single Day Event
  isSingleDayEvent: Yup.boolean().optional(),

  // End Date - Optional field
  endDate: Yup.string()
    .optional()
    .test('required-if-multi-day', 'End date is required for multi-day events', function(value) {
      const { isSingleDayEvent } = this.parent;
      if (isSingleDayEvent === false && !value) {
        return false;
      }
      return true;
    })
    .test('future-date', 'End date must be in the future', function(value) {
      if (!value) return true;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    })
    .test('after-start-date', 'End date must be after or equal to start date', function(value, context) {
      const { eventDate, isSingleDayEvent } = context.parent;
      if (isSingleDayEvent === true || !value || !eventDate) return true;
      const start = new Date(eventDate);
      const end = new Date(value);
      return end >= start;
    }),

  // Event Image - Required field
  eventImage: Yup.mixed()
    .required('Event image is required')
    .test('file-size', 'File size must be less than 5MB', function(value) {
      if (!value) return true;
      return (value as File).size <= 5 * 1024 * 1024; // 5MB limit
    })
    .test('file-type', 'Only image files are allowed', function(value) {
      if (!value) return true;
      return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes((value as File).type);
    }),

  // Event Description - Required field
  eventDescription: Yup.string()
    .required('Event description is required')
    .min(20, 'Event description must be at least 20 characters')
    .max(2000, 'Event description must be less than 2000 characters'),

  // Short Description - Optional but with validation if provided
  shortDescription: Yup.string()
    .max(160, 'Short description must be less than 160 characters for SEO')
    .test('min-length-if-provided', 'Short description must be at least 10 characters if provided', function(value) {
      if (!value) return true; // Optional field
      return value.length >= 10;
    }),

  // Venue - Required field (for single venue mode, only for offline events)
  venue: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema
        .required('Venue is required')
        .min(2, 'Venue must be at least 2 characters')
        .max(200, 'Venue must be less than 200 characters'),
      otherwise: (schema) => schema.optional(),
    }),

  // Address fields - Optional (for single venue mode, only for offline events)
  addressLine1: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema.max(200, 'Address line 1 must be less than 200 characters'),
      otherwise: (schema) => schema.optional(),
    }),

  addressLine2: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema.max(200, 'Address line 2 must be less than 200 characters'),
      otherwise: (schema) => schema.optional(),
    }),

  // Country - Required field (for single venue mode, only for offline events)
  country: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema.required('Country is required'),
      otherwise: (schema) => schema.optional(),
    }),

  // State - Optional (for single venue mode, only for offline events)
  state: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema.max(100, 'State must be less than 100 characters'),
      otherwise: (schema) => schema.optional(),
    }),

  // City - Optional but recommended (for single venue mode, only for offline events)
  city: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema.max(100, 'City must be less than 100 characters'),
      otherwise: (schema) => schema.optional(),
    }),

  // Zip Code - Optional but with format validation if provided (for single venue mode, only for offline events)
  zipCode: Yup.string()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => !hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema
        .max(20, 'Zip code must be less than 20 characters')
        .test('zip-format', 'Please enter a valid zip code format', function(value) {
          if (!value) return true; // Optional field
          // Basic zip code validation (can be customized based on country)
          return /^[A-Za-z0-9\s\-]+$/.test(value);
        }),
      otherwise: (schema) => schema.optional(),
    }),

  // Event Type - Required field
  isOnlineEvent: Yup.boolean().required(),

  // Multiple Venues - Required when hasMultipleVenues is true (only for offline events)
  hasMultipleVenues: Yup.boolean().required(),

  venues: Yup.array()
    .when(['hasMultipleVenues', 'isOnlineEvent'], {
      is: (hasMultipleVenues: boolean, isOnlineEvent: boolean) => hasMultipleVenues && !isOnlineEvent,
      then: (schema) => schema
        .min(2, 'At least two venues are required when using multiple venues')
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            name: Yup.string()
              .required('Venue name is required')
              .min(2, 'Venue name must be at least 2 characters')
              .max(200, 'Venue name must be less than 200 characters'),
            addressLine1: Yup.string().max(200, 'Address line 1 must be less than 200 characters'),
            addressLine2: Yup.string().max(200, 'Address line 2 must be less than 200 characters'),
            country: Yup.string(),
            state: Yup.string().max(100, 'State must be less than 100 characters'),
            city: Yup.string().max(100, 'City must be less than 100 characters'),
            zipCode: Yup.string()
              .max(20, 'Zip code must be less than 20 characters')
              .test('zip-format', 'Please enter a valid zip code format', function(value) {
                if (!value) return true; // Optional field
                return /^[A-Za-z0-9\s\-]+$/.test(value);
              }),
            eventDate: Yup.string()
              .test('future-date', 'Event date must be in the future', function(value) {
                if (!value) return true; // Optional field
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return selectedDate >= today;
              }),
          })
        ),
      otherwise: (schema) => schema.optional(),
    }),

  // Categories - Optional but recommended
  categories: Yup.array()
    .of(Yup.string())
    .max(5, 'You can select up to 5 categories'),

  // Duration - Optional
  duration: Yup.string()
    .max(50, 'Duration must be less than 50 characters'),
});

// Validation schema for Ticket Modal
export const ticketValidationSchema = Yup.object().shape({
  ticketType: Yup.string()
    .required('Ticket name is required')
    .min(2, 'Ticket name must be at least 2 characters')
    .max(100, 'Ticket name must be less than 100 characters'),

  description: Yup.string()
    .required('Ticket description is required')
    .min(10, 'Ticket description must be at least 10 characters')
    .max(500, 'Ticket description must be less than 500 characters'),

  price: Yup.number()
    .required('Ticket price is required')
    .min(0, 'Price must be 0 or greater')
    .max(999999, 'Price must be less than 999,999'),

  maxCount: Yup.number()
    .required('Maximum ticket count is required')
    .min(0, 'Maximum count must be 0 or greater')
    .max(999999, 'Maximum count must be less than 999,999'),

  groupTicketCount: Yup.number()
    .when('isGroupTicket', {
      is: true,
      then: (schema) => schema
        .required('Number of people per group is required')
        .min(2, 'Group capacity must be at least 2 attendees')
        .max(100, 'Maximum 100 people per group'),
      otherwise: (schema) => schema.optional()
    }),

  isGroupTicket: Yup.boolean(),
});

// Validation schema for complete event form
export const completeEventValidationSchema = Yup.object().shape({
  // Details tab
  eventName: eventDetailsValidationSchema.fields.eventName,
  eventDate: eventDetailsValidationSchema.fields.eventDate,
  endDate: eventDetailsValidationSchema.fields.endDate,
  eventTime: eventDetailsValidationSchema.fields.eventTime,
  eventEndTime: eventDetailsValidationSchema.fields.eventEndTime,
  eventImage: eventDetailsValidationSchema.fields.eventImage,
  eventDescription: eventDetailsValidationSchema.fields.eventDescription,
  shortDescription: eventDetailsValidationSchema.fields.shortDescription,
  venue: eventDetailsValidationSchema.fields.venue,
  addressLine1: eventDetailsValidationSchema.fields.addressLine1,
  addressLine2: eventDetailsValidationSchema.fields.addressLine2,
  country: eventDetailsValidationSchema.fields.country,
  state: eventDetailsValidationSchema.fields.state,
  city: eventDetailsValidationSchema.fields.city,
  zipCode: eventDetailsValidationSchema.fields.zipCode,
  categories: eventDetailsValidationSchema.fields.categories,
  duration: eventDetailsValidationSchema.fields.duration,

  // Tickets tab
  tickets: Yup.array()
    .of(ticketValidationSchema)
    .min(1, 'At least one ticket is required')
    .max(20, 'Maximum 20 tickets allowed'),

  // Settings tab
  showOtherFees: Yup.boolean(),
  isOtherAttendeeRequired: Yup.boolean(),
  hasTermsAndConditions: Yup.boolean(),
  termsAndConditions: Yup.string()
    .when('hasTermsAndConditions', {
      is: true,
      then: (schema) => schema
        .required('Terms and conditions are required when enabled')
        .min(10, 'Terms and conditions must be at least 10 characters'),
      otherwise: (schema) => schema.optional()
    }),
});

// Helper function to get field error
export const getFieldError = (touched: any, errors: any, fieldName: string): string | undefined => {
  return touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined;
};

// Helper function to check if field is invalid
export const isFieldInvalid = (touched: any, errors: any, fieldName: string): boolean => {
  return !!(touched[fieldName] && errors[fieldName]);
}; 